/**
 * Evaluation Page Component
 * Wrapper that fetches evaluation data and renders FinalEvaluation
 * Fetches both evaluation data and candidate info to ensure proper display
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FinalEvaluation } from '../../v4/components/FinalEvaluation';
import { API_BASE, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

interface EvaluationPageProps {
    candidateId: string;
    onBack?: () => void;
}

interface CandidateInfo {
    id: string;
    name: string;
    email: string;
    simulation_title?: string;
    position_applied?: string;
    session_id?: string;
    user_id?: string;
}

export function EvaluationPage({ candidateId, onBack }: EvaluationPageProps) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [evaluationData, setEvaluationData] = useState<any | null>(null);

    useEffect(() => {
        loadData();
    }, [candidateId]);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            const authHeaders = await getAuthHeaders();
            
            // Fetch both evaluation data and candidate info in parallel
            const [evaluationResponse, candidatesResponse] = await Promise.all([
                axios.get(`${API_BASE}/candidates/${candidateId}/evaluation`, { headers: authHeaders }),
                axios.get(`${API_BASE}/candidates`, { headers: authHeaders })
            ]);

            // Process evaluation data
            if (evaluationResponse.data.data) {
                const evalData = evaluationResponse.data.data;
                
                // Find the candidate in the list to get name and simulation title
                const candidates = candidatesResponse.data.data?.candidates || [];
                
                console.log('EvaluationPage - Looking for candidateId:', candidateId);
                console.log('EvaluationPage - Candidates list length:', candidates.length);
                
                // Try multiple ways to find the matching candidate
                let candidate: CandidateInfo | undefined;
                
                // First, direct ID match (id is the session_id)
                candidate = candidates.find((c: CandidateInfo) => c.id === candidateId);
                
                // Try session_id field match
                if (!candidate) {
                    candidate = candidates.find((c: CandidateInfo) => c.session_id === candidateId);
                }
                
                // Try matching by session_id in eval data
                if (!candidate && evalData.session_id) {
                    candidate = candidates.find((c: CandidateInfo) => 
                        c.id === evalData.session_id || 
                        c.session_id === evalData.session_id
                    );
                }
                
                console.log('EvaluationPage - Found candidate:', candidate);
                
                // Determine the best values for candidate name and simulation title
                let candidateName = 'Candidate';
                let simulationTitle = 'Simulation';
                
                // Priority 1: Non-default values from evaluation data
                if (evalData.candidate_name && 
                    evalData.candidate_name !== 'Candidate' && 
                    !evalData.candidate_name.startsWith('user_') &&
                    !evalData.candidate_name.startsWith('session_')) {
                    candidateName = evalData.candidate_name;
                }
                // Priority 2: Values from candidate record
                else if (candidate?.name && 
                         candidate.name !== 'Candidate' && 
                         !candidate.name.startsWith('user_') &&
                         !candidate.name.startsWith('session_')) {
                    candidateName = candidate.name;
                }
                // Priority 3: User's name from auth context
                else if (user?.fullName) {
                    candidateName = user.fullName;
                } else if (user?.firstName) {
                    candidateName = `${user.firstName} ${user.lastName || ''}`.trim();
                }
                
                // Simulation title priority
                if (evalData.simulation_title && evalData.simulation_title !== 'Simulation') {
                    simulationTitle = evalData.simulation_title;
                } else if (candidate?.simulation_title && candidate.simulation_title !== 'Simulation') {
                    simulationTitle = candidate.simulation_title;
                }
                
                // Enrich evaluation data with candidate info
                const enrichedEvalData = {
                    ...evalData,
                    candidate_name: candidateName,
                    simulation_title: simulationTitle
                };
                
                console.log('EvaluationPage - Final values:', {
                    candidateName,
                    simulationTitle,
                    evalDataCandidateName: evalData.candidate_name,
                    evalDataSimTitle: evalData.simulation_title,
                    candidateRecordName: candidate?.name,
                    candidateRecordSimTitle: candidate?.simulation_title,
                    userFullName: user?.fullName
                });
                
                setEvaluationData(enrichedEvalData);
            } else {
                setError('No evaluation data found');
            }
        } catch (err: any) {
            console.error('Failed to load evaluation:', err);
            setError(err.response?.data?.error || 'Failed to load evaluation');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/app/results');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading evaluation...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Evaluation</h2>
                    <p className="text-slate-600 mb-4">{error}</p>
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
                    >
                        Back to Results
                    </button>
                </div>
            </div>
        );
    }

    if (!evaluationData) {
        return null;
    }

    return (
        <FinalEvaluation
            evaluation={evaluationData}
            onClose={handleClose}
            candidateName={evaluationData.candidate_name}
            simulationTitle={evaluationData.simulation_title}
        />
    );
}

export default EvaluationPage;
